import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, MapPin, Star, ArrowRight, Loader2, ShieldCheck, Users, MessagesSquare } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";
import { caseDisplayName } from "@/lib/caseName";

const photoUrl = (p) => (p ? p.external_url || (p.storage_path ? `${API}/photos/file/${p.storage_path}` : null) : null);
const coverOf = (c) => photoUrl(c.primary_photo) || c.thumbnail;

function PickCard({ c, onPick, picking, selected }) {
  const cover = coverOf(c);
  return (
    <button
      type="button"
      data-testid={`welcome-case-${c.slug}`}
      onClick={() => onPick(c)}
      disabled={picking}
      className={`group text-left flex flex-col rounded-md overflow-hidden card-surface transition-colors ${
        selected ? "border-gold" : "hover:border-gold/40"
      } disabled:opacity-60`}
    >
      <div className="relative h-32 overflow-hidden">
        {cover && <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />}
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-transparent" />
        <span className="absolute top-3 left-3 label-mono bg-charcoal/80 backdrop-blur px-2 py-1 rounded-sm border border-white/10">{c.status}</span>
        {selected && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-sm bg-gold/90 text-charcoal font-bold">
            {picking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />} Selected
          </span>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-display text-lg font-semibold text-white leading-tight group-hover:text-gold transition-colors">{caseDisplayName(c.title)}</h3>
        {c.location && <p className="mt-1.5 flex items-center gap-1.5 text-xs text-white/40 font-mono"><MapPin className="w-3 h-3" /> {c.location}</p>}
        <p className="mt-2 text-sm text-white/50 leading-relaxed line-clamp-2">{c.summary}</p>
        <div className="mt-3 flex items-center gap-4 text-xs text-white/40 font-mono">
          <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {c.follower_count ?? 0}</span>
          <span className="flex items-center gap-1.5"><MessagesSquare className="w-3.5 h-3.5" /> {c.thread_count ?? 0}</span>
        </div>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold">
          Follow this case free <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </button>
  );
}

export default function WelcomePickCase() {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(true);
  const [query, setQuery] = useState("");
  const [picking, setPicking] = useState(null);
  const decidedRef = useRef(false);

  const isMember = user?.role === "admin" || user?.subscription_status === "active";

  useEffect(() => {
    if (loading || decidedRef.current) return;
    if (!user) { decidedRef.current = true; navigate("/register", { replace: true }); return; }
    // Decide once, on first load: members / already-chosen users skip onboarding.
    // After a pick sets free_case_id we must NOT re-trigger this redirect.
    decidedRef.current = true;
    if (isMember || user.free_case_id) navigate("/dashboard", { replace: true });
  }, [user, loading, isMember, navigate]);

  useEffect(() => {
    api.get("/public/cases")
      .then(({ data }) => setCases((data || []).filter((c) => c.kind !== "special")))
      .catch(() => {})
      .finally(() => setLoadingCases(false));
  }, []);

  const pick = async (c) => {
    if (picking) return;
    setPicking(c.case_id);
    try {
      await api.post(`/cases/${c.case_id}/follow`);
      await refreshUser();
      toast.success(`${caseDisplayName(c.title)} is now your free case — jump in.`);
      navigate(`/cases/${c.slug}`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not set your free case");
      setPicking(null);
    }
  };

  const q = query.trim().toLowerCase().replace(/^the\s+/i, "");
  const filtered = q
    ? cases.filter((c) => `${c.title || ""} ${c.location || ""} ${c.summary || ""}`.toLowerCase().includes(q))
    : cases;

  return (
    <div className="min-h-screen bg-charcoal">
      <div className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/pricing" data-testid="welcome-upgrade-link" className="text-sm text-white/50 hover:text-gold transition-colors">
            Want every case? Go Founding →
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 label-mono mb-4 px-3 py-1.5 border border-gold/40 rounded-sm bg-gold/10 text-gold">
            <ShieldCheck className="w-4 h-4" /> Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-bold text-white leading-tight">
            Choose your <span className="text-gold">free case</span>.
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl">
            Your free account follows one case at a time. Pick the case you want to work — you'll get its overview,
            basic timeline, and the member discussion. You can switch later, or upgrade anytime to follow them all.
          </p>
        </motion.div>

        <div className="relative max-w-xl my-8">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search cases by name, location…"
            data-testid="welcome-search-input"
            className="w-full bg-white/5 border border-white/10 rounded-sm pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold/50 transition-colors"
          />
        </div>

        {loadingCases ? (
          <div className="label-mono animate-pulse py-20 text-center">Loading cases…</div>
        ) : filtered.length === 0 ? (
          <div className="card-surface rounded-md p-12 text-center text-white/40" data-testid="welcome-empty">
            {q ? `No cases match “${query}”.` : "No cases available yet."}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="welcome-case-grid">
            {filtered.map((c, i) => (
              <motion.div key={c.case_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.3) }}>
                <PickCard c={c} onPick={pick} picking={!!picking} selected={picking === c.case_id} />
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-10 text-center">
          <Link to="/cases" data-testid="welcome-skip-link" className="text-sm text-white/50 hover:text-gold transition-colors">
            Not sure yet? Browse the full directory first →
          </Link>
        </div>
      </div>
    </div>
  );
}
